import React, { useState, Fragment, useEffect } from "react";
import { Grid, notification } from "antd";
import { useTranslation, Trans } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { generateDynamicRecords, localStorageHelper } from "utils";
import { Button, Box, Pagination, Select } from "components";
import { PlusIcon, ArrowNextIcon, ConfirmFailIcon } from "icons";
import { IOption } from "components/select/Select";

import { StressCheckListData } from "models/stressCheck/stressCheckList";
import {
  GetStressCheckListActionCreators,
  CreateStressCheckActionCreators,
  UpdateStressCheckActionCreators,
} from "redux/rootActions";
import {
  stressCheckListDataResponse,
  selectIsLoading,
  selectErrorMessage,
} from "redux/stressCheck/stressCheckList/stressCheckListStates";

import {
  PaginationWrapper,
  ContentWrapper,
  TableStyled,
  Dot,
  StatusWrapper,
  WrapperSizeChange,
  TextTitle,
  TitleContainer,
  EmptyStressCheckNotice,
} from "./Stress.style";
import NewStressCheck from "./NewStressCheck";
import StressDetails from "./StressDetails";
import EditDraft from "./EditDraft";

type ScreenStatus = "create" | "details" | "list" | "draft" | "";

const { useBreakpoint } = Grid;

const StressManagement: React.FC = () => {
  const { t }: any = useTranslation("translation", {
    keyPrefix: "stress-check",
  });
  const { t: t2 }: any = useTranslation("translation", {
    keyPrefix: "user-list",
  });
  const { t: t3 }: any = useTranslation("translation", {
    keyPrefix: "mental-health",
  });

  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const StressCheckListResponse = useSelector(stressCheckListDataResponse);
  const isLoadingStressCheckList = useSelector(selectIsLoading);
  const getStressCheckListError = useSelector(selectErrorMessage);

  const [screenStatus, setScreenStatus] = useState<ScreenStatus>("list");
  const [testId, setTestId] = useState();
  const [input, setInput] = useState({
    limit: 50,
    page: 1,
  });
  const [recordOptions, setRecordOptions] = useState<IOption[]>([]);
  const totalRecord = StressCheckListResponse?.total_record || 0;

  const getStressCheckList = () => {
    const paramsStressCheckList: StressCheckListData = {
      page: input.page,
      per_page: input.limit,
      language_code: currentLanguage || "ja",
      locale: currentLanguage || "ja",
    };
    dispatch(
      GetStressCheckListActionCreators.stressCheckListAction(
        paramsStressCheckList
      )
    );
  };

  useEffect(() => {
    if (screenStatus === "list") {
      getStressCheckList();
    }
  }, [input.page, input.limit, screenStatus]);

  useEffect(() => {
    if (
      StressCheckListResponse &&
      Object.keys(StressCheckListResponse).length
    ) {
      let recordOptionsClone: IOption[] = [];
      recordOptionsClone = generateDynamicRecords(
        StressCheckListResponse?.total_record,
        t2
      );
      setRecordOptions([...recordOptionsClone]);
    }
  }, [StressCheckListResponse]);

  useEffect(() => {
    dispatch(GetStressCheckListActionCreators.handleResetDataAction());
    dispatch(CreateStressCheckActionCreators.handleResetDataAction());
    dispatch(UpdateStressCheckActionCreators.handleResetDataAction());
    setTestId(undefined);
  }, []);

  useEffect(() => {
    if (getStressCheckListError) {
      notification.error({
        placement: "bottomRight",
        message: getStressCheckListError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(GetStressCheckListActionCreators.handleResetDataAction());
    }
  }, [getStressCheckListError]);

  const returnColor = (status: string) => {
    if (status) {
      switch (status) {
        case "実施中":
          return "#2AC769";
          break;
        case "実施待ち":
          return "#EBC300";
          break;
        case "実施完了":
          return "#E0E0E0";
          break;
        case "下書き":
          return "#B7B7B7";
          break;
        default:
          return "#2AC769";
      }
    }
  };

  const returnStressType = (type: number) => {
    if (type) {
      switch (type) {
        case 1:
          return `25${t3("item")}`;
          break;
        case 2:
          return `59${t3("item")}`;
          break;
      }
    }
  };

  const columns = [
    {
      title: t("number-of-items"),
      dataIndex: "stress_check_type_id",
      key: "stress_check_type_id",
      width: 136,
      fixed: window.innerWidth < 576 ? "" : "left",
      render: (text: any, record: any) => (
        <div>{returnStressType(record?.stress_check_type_id)}</div>
      ),
    },
    {
      title: t3("test-name"),
      dataIndex: "name",
      key: "name",
      width: 316,
    },
    {
      title: t3("examination-period"),
      dataIndex: "examination_period",
      key: "examination_period",
      width: 506,
    },
    {
      title: t3("implementation-status"),
      dataIndex: "implement_status",
      key: "implement_status",
      render: (text: any, record: any) => (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <StatusWrapper>
            <Dot
              style={{
                background: returnColor(record?.implement_status),
              }}
            />
            <span
              style={{
                color:
                  record?.implement_status === "実施完了"
                    ? "#E0E0E0"
                    : "#3B3B3B",
              }}
            >
              {text}
            </span>
          </StatusWrapper>
          <ArrowNextIcon />
        </div>
      ),
    },
  ];

  const handleChangePage = (page: number) => {
    setInput((prevState: any) => ({ ...prevState, page }));
  };

  const handleOnChangeSizeChange = (limit: number) => {
    setInput((prevState: any) => ({ ...prevState, limit, page: 1 }));
  };

  const handleCancel = () => {
    setScreenStatus("list");
  };

  const ShowPagination = () => {
    return totalRecord > input.limit ? (
      <PaginationWrapper>
        <Pagination
          current={input.page}
          onChange={handleChangePage}
          pageSize={input.limit}
          total={10}
          showSizeChanger={false}
          showLessItems={!screens.xl}
        />
      </PaginationWrapper>
    ) : (
      <Fragment></Fragment>
    );
  };

  const renderContents = () => {
    switch (screenStatus) {
      case "list":
        return (
          <Box title={t("stress-check-management")} padding="16px 16px 32px">
            <TitleContainer
              style={{
                marginBottom: 32,
              }}
            >
              <TextTitle>{t("stress-check-list")}</TextTitle>
              <Button
                color="#FFFFFF"
                fontSize={16}
                fontWeight={700}
                lineHeight="16px"
                background={"#2AC769"}
                padding="12px 24px 12px 6px"
                name={t3("create")}
                bdr="6px"
                border="none"
                icon={
                  <PlusIcon
                    width="14px"
                    height="14px"
                    fill="#FFFFFF"
                    style={{ marginRight: 4 }}
                  />
                }
                onClick={() => {
                  setScreenStatus("create");
                }}
              />
            </TitleContainer>
            <TitleContainer
              style={{
                alignItems: "flex-start",
              }}
            >
              <WrapperSizeChange>
                <div style={{ fontWeight: 500 }}>{t2("record")}</div>
                <Select
                  defaultValue={50}
                  options={recordOptions}
                  onChange={handleOnChangeSizeChange}
                />
              </WrapperSizeChange>
              <ShowPagination />
            </TitleContainer>
            <ContentWrapper>
              <TableStyled
                dataSource={StressCheckListResponse?.result}
                columns={columns}
                scroll={{ x: "max-content" }}
                loading={isLoadingStressCheckList}
                onRow={(record: any) => {
                  return {
                    onClick: () => {
                      if (record?.implement_status === "下書き") {
                        setScreenStatus("draft");
                      } else {
                        setScreenStatus("details");
                      }
                      setTestId(record?.id);
                    },
                  };
                }}
              />
              {!StressCheckListResponse?.result?.length && (
                <EmptyStressCheckNotice>
                  <Trans i18nKey="stress-check.empty-stress-warning">
                    ストレスチェックはまだ実施されていません。右上の「
                    <span>＋新規作成</span>
                    」ボタンより、ストレスチェックを作成してください。
                  </Trans>
                </EmptyStressCheckNotice>
              )}
            </ContentWrapper>
            <ShowPagination />
          </Box>
        );
      case "create":
        return <NewStressCheck handleCancel={handleCancel} />;
      case "draft":
        return <EditDraft handleCancel={handleCancel} id={testId} />;
      case "details":
        return <StressDetails handleCancel={handleCancel} id={testId} />;
      default:
        return;
    }
  };

  return <>{renderContents()}</>;
};

export default StressManagement;
